import { AlertTriangle, Info } from 'lucide-react';
import { fmtPln } from './client-offer-review-format';
import type { ClientOfferReviewSummaryStatsLabels } from './client-offer-review-types';

/** Bordered stats block — left compact column + right decision breakdown/progress bar. */
export function ClientOfferReviewSummaryStats({
  unitCount,
  cityCount,
  campaignRange,
  totalNet,
  hasSold,
  soldShort,
  soldLabel,
  accCount,
  rejCount,
  pendCount,
  accValue,
  pctAcc,
  pctRej,
  pctPend,
  availCount,
  labels,
}: {
  unitCount: number;
  cityCount: number;
  campaignRange: string;
  totalNet: number;
  hasSold: boolean;
  soldShort: string;
  soldLabel: string;
  accCount: number;
  rejCount: number;
  pendCount: number;
  accValue: number;
  pctAcc: number;
  pctRej: number;
  pctPend: number;
  availCount: number;
  labels: ClientOfferReviewSummaryStatsLabels;
}) {
  return (
    <div
      data-co-statsrow
      className="grid shrink-0 grid-cols-[auto_1fr] overflow-hidden rounded-xl border border-border/70 tabular-nums"
    >
      <div className="grid grid-cols-2 content-center gap-x-4 gap-y-0.5 border-r border-border bg-muted/60 px-4 py-2.5 text-xs">
        <span className="text-muted-foreground">{labels.unitsLabel}</span>
        <span className="text-right font-semibold">
          {unitCount} · {cityCount} {labels.citiesUnit}
        </span>
        <span className="text-muted-foreground">{labels.campaignLabel}</span>
        <span className="text-right font-semibold">{campaignRange}</span>
        <span className="text-muted-foreground">{labels.totalNetLabel}</span>
        <span className="text-right font-semibold text-primary">{fmtPln(totalNet)}</span>
        {hasSold ? (
          <span
            title={soldLabel}
            className="col-span-2 mt-0.5 inline-flex cursor-help items-center gap-1.5 text-2xs text-red-700"
          >
            <AlertTriangle className="h-3 w-3" strokeWidth={2} />
            {soldShort}
          </span>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-col justify-center gap-1.5 px-4 py-2.5">
        <div className="flex flex-wrap items-baseline gap-4 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <i className="inline-block h-2 w-2 rounded-xs bg-status-free" />
            {labels.accepted} <b className="text-emerald-800">{accCount}</b>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="inline-block h-2 w-2 rounded-xs bg-status-sold" />
            {labels.rejected} <b className="text-red-800">{rejCount}</b>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="inline-block h-2 w-2 rounded-xs bg-border" />
            {labels.pending} <b>{pendCount}</b>
            <span title={labels.pendingHint} className="inline-flex cursor-help">
              <Info className="h-3 w-3 text-muted-foreground" strokeWidth={2} />
            </span>
          </span>
          <span className="ml-auto text-muted-foreground">
            {labels.acceptedValueLabel} <b className="text-foreground">{fmtPln(accValue)}</b>
          </span>
        </div>
        <div className="flex h-2 overflow-hidden rounded-full bg-muted">
          <div
            className="bg-status-free transition-[width] duration-200"
            style={{ width: `${pctAcc}%` }}
          />
          <div
            className="bg-status-sold transition-[width] duration-200"
            style={{ width: `${pctRej}%` }}
          />
          <div
            className="bg-border transition-[width] duration-200"
            style={{ width: `${pctPend}%` }}
          />
        </div>
        <div className="flex justify-between gap-3 text-2xs text-muted-foreground">
          <span>
            {labels.breakdownSummary(accCount, pctAcc, rejCount, pctRej, pendCount, pctPend)}
          </span>
          <span>{labels.availableUnitsSuffix(availCount)}</span>
        </div>
      </div>
    </div>
  );
}
